import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Dimensions, Animated, RefreshControl, TextInput, Modal, Alert,
  ActivityIndicator, StatusBar, Platform, KeyboardAvoidingView, Share,
  Switch, BackHandler,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');

// ─── API CONFIG ───────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const CACHE_TTL = 15 * 60 * 1000;
let CACHE_KEY = 'erp_system_cache_v4';

const api = axios.create({ baseURL: API_BASE, timeout: 12000, headers: { 'Content-Type': 'application/json' } });
api.interceptors.request.use(async (config) => {
  try { const t = await AsyncStorage.getItem('auth_token'); if (t) config.headers.Authorization = `Bearer ${t}`; } catch {}
  return config;
});

// ─── BRAND ───────────────────────────────────────────────────────────────────
const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6',
};
const DARK = {
  blue: '#3b82f6', canary: '#fbbf24', mint: '#06b6d4',
  bg: '#0f172a', surface: '#1e293b', text: '#f1f5f9', sub: '#94a3b8',
  line: '#334155', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#0ea5e9', purple: '#8b5cf6',
};

// ─── MODULES ──────────────────────────────────────────────────────────────────
const ERP_MODULES = [
  {
    id: 'crm', name: 'CRM & Sales', icon: 'handshake', color: '#f97316',
    desc: 'Customer Relationship Management', category: 'Sales', load: 85, version: '4.1',
    subModules: ['Lead Mgmt', 'Contacts', 'Deals', 'Pipeline'],
    kpis: [{ label: 'Customers', value: '1,847', trend: '+12%', icon: 'account-group' }, { label: 'Open Deals', value: '156', trend: '+8%', icon: 'briefcase' }, { label: 'Won This Month', value: '23', trend: '+15%', icon: 'trophy' }, { label: 'Conversion', value: '24.8%', trend: '+3%', icon: 'trending-up' }],
  },
  {
    id: 'finance', name: 'Finance & Accounting', icon: 'cash-multiple', color: '#10b981',
    desc: 'Complete Financial Management Suite', category: 'Core', load: 75, version: '3.2',
    subModules: ['General Ledger', 'Accounts', 'Tax', 'Budgeting'],
    kpis: [{ label: 'Revenue MTD', value: '£2.45M', trend: '+18%', icon: 'trending-up' }, { label: 'Expenses', value: '£1.89M', trend: '-3%', icon: 'trending-down' }, { label: 'Net Profit', value: '£560k', trend: '+22%', icon: 'cash' }, { label: 'Cash Flow', value: '£450k', trend: '+11%', icon: 'bank' }],
  },
  {
    id: 'hr', name: 'Human Resources', icon: 'account-group', color: '#8b5cf6',
    desc: 'HR & Employee Lifecycle Management', category: 'Core', load: 60, version: '2.4',
    subModules: ['Employees', 'Payroll', 'Leave', 'Performance'],
    kpis: [{ label: 'Employees', value: '245', trend: '+5', icon: 'account' }, { label: 'New Hires', value: '12', trend: 'this month', icon: 'account-plus' }, { label: 'Turnover', value: '8.2%', trend: '-1.2%', icon: 'account-remove' }, { label: 'Avg Salary', value: '£45k', trend: '+3%', icon: 'currency-gbp' }],
  },
  {
    id: 'inventory', name: 'Inventory Management', icon: 'warehouse', color: '#19abb5',
    desc: 'Stock & Warehouse Management', category: 'Operations', load: 45, version: '2.3',
    subModules: ['Stock Control', 'Warehouses', 'Transfers', 'Reorders'],
    kpis: [{ label: 'Total SKUs', value: '1,247', trend: '+34', icon: 'package' }, { label: 'Stock Value', value: '£890k', trend: '+7%', icon: 'cash' }, { label: 'Low Stock', value: '23', trend: '!', icon: 'alert' }, { label: 'Turnover', value: '4.2x', trend: '+0.3x', icon: 'refresh' }],
  },
  {
    id: 'analytics', name: 'Analytics & BI', icon: 'chart-bar', color: '#0e4e96',
    desc: 'Business Intelligence & Analytics', category: 'Intelligence', load: 100, version: '3.5',
    subModules: ['Dashboards', 'Reports', 'KPIs', 'Forecasting'],
    kpis: [{ label: 'Active Reports', value: '18', trend: '+4', icon: 'file-chart' }, { label: 'Dashboards', value: '7', trend: '+2', icon: 'view-dashboard' }, { label: 'KPIs Tracked', value: '42', trend: '+8', icon: 'flag' }, { label: 'Data Points', value: '2.4M', trend: '+12%', icon: 'database' }],
  },
  {
    id: 'projects', name: 'Project Management', icon: 'clipboard-check', color: '#6366f1',
    desc: 'Project Planning & Execution', category: 'Operations', load: 65, version: '2.8',
    subModules: ['Projects', 'Tasks', 'Resources', 'Timeline'],
    kpis: [{ label: 'Active Projects', value: '34', trend: '+6', icon: 'folder' }, { label: 'Tasks Due', value: '87', trend: '-12', icon: 'check-circle' }, { label: 'On-Time Rate', value: '87%', trend: '+2%', icon: 'clock-check' }, { label: 'Budget Used', value: '68%', trend: '-5%', icon: 'cash-multiple' }],
  },
  {
    id: 'payroll', name: 'Payroll', icon: 'currency-gbp', color: '#ec4899',
    desc: 'Payroll Processing & Compliance', category: 'Core', load: 55, version: '2.1',
    subModules: ['Pay Runs', 'PAYE', 'Benefits', 'Pension'],
    kpis: [{ label: 'Monthly Payroll', value: '£920k', trend: '+3%', icon: 'bank-transfer' }, { label: 'Employees Paid', value: '245', trend: '=', icon: 'account-check' }, { label: 'PAYE Due', value: '£186k', trend: 'due in 8d', icon: 'bank-outline' }, { label: 'Pension', value: '£92k', trend: 'this month', icon: 'shield-account' }],
  },
  {
    id: 'procurement', name: 'Procurement', icon: 'truck-delivery', color: '#f59e0b',
    desc: 'Supplier & Purchase Order Management', category: 'Operations', load: 40, version: '1.9',
    subModules: ['Purchase Orders', 'Suppliers', 'Contracts', 'Budgets'],
    kpis: [{ label: 'Open POs', value: '43', trend: '+7', icon: 'file-document' }, { label: 'Suppliers', value: '128', trend: '+12', icon: 'domain' }, { label: 'Spend MTD', value: '£340k', trend: '+9%', icon: 'cash' }, { label: 'Savings', value: '£28k', trend: 'this month', icon: 'piggy-bank' }],
  },
  {
    id: 'production', name: 'Manufacturing', icon: 'factory', color: '#ef4444',
    desc: 'Manufacturing & Production Planning', category: 'Operations', load: 70, version: '2.6',
    subModules: ['Work Orders', 'BOM', 'Quality Control', 'Scheduling'],
    kpis: [{ label: 'Work Orders', value: '28', trend: '+5', icon: 'clipboard-list' }, { label: 'Output Today', value: '1,240', trend: '+6%', icon: 'factory' }, { label: 'OEE', value: '78%', trend: '+3%', icon: 'gauge' }, { label: 'Defect Rate', value: '0.8%', trend: '-0.2%', icon: 'alert-circle' }],
  },
];

// ─── RICH MOCK DATA ───────────────────────────────────────────────────────────
// MOCK data removed — using real API data

// ─── AI SERVICE ───────────────────────────────────────────────────────────────
let aiService = null;
try { aiService = require('../../../services/aiService'); } catch {}

const askERPAI = async (prompt, context) => {
  if (aiService?.getAIResponse) {
    try { return await aiService.getAIResponse(prompt, context); } catch {}
  }
  try {
    const res = await fetch('https://api.jobeng.ai/api/ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514', max_tokens: 600,
        system: context,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    const data = await res.json();
    return (data.text || data.content || data.choices?.[0]?.message?.content || '');
  } catch {}
  const fallbacks = {
    revenue: `Based on your ERP data, revenue is tracking at ${overview.revenue} with ${overview.growth} growth. Focus on the 156 open deals to accelerate Q2 performance.`,
    inventory: `23 products are below reorder levels. Prioritise USB-C Cable (12 units) and Laptop Pro (8 units). Estimated restocking cost: £34,000.`,
    hr: `With 245 employees and 12 new hires this month, your headcount is growing. Turnover at 8.2% is within healthy range. Next payroll run: 8 days.`,
    default: `Your ERP shows strong fundamentals: ${overview.revenue} revenue (+18.2%), ${overview.customers} customers, ${overview.employees} employees. Key priorities: 1) Close 23 low-stock POs 2) Follow up on 3 overdue invoices 3) Review 7 pending approvals`,
  };
  const k = Object.keys(fallbacks).find(key => prompt.toLowerCase().includes(key)) || 'default';
  return fallbacks[k];
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const EnhancedERPSystemScreen = ({ navigation, route }) => {
  const [darkMode, setDarkMode] = useState(false);
  const T = darkMode ? DARK : BRAND;

  // Data state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Local AI state — calls the /api/ai proxy directly with real data embedded
  // in the prompt, so the assistant always has actual figures to analyse.
  // (Bypasses the agent path which could send an empty context.)
  const [aiHistory, setAiHistory] = useState([
    { role: 'assistant', text: 'Hi! Ask me anything about your business — I have your live ERP data.', ts: Date.now() },
  ]);
  const [aiLoading, setAiLoading] = useState(false);
  const aiAgents = [];
  const [showAIChat, setShowAIChat] = useState(false);

  const [user, setUser] = useState({ id: '', name: '', email: '', role: 'Admin', dept: '', avatar: 'U', joinDate: '', lastLogin: new Date().toISOString() });
  const [overview, setOverview] = useState({ revenue: '\u00A30', growth: '0%', customers: 0, employees: 0, projects: 0, satisfaction: '\u2014', profitMargin: '0%', cashFlow: '\u00A30' });
  const [activities, setActivities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [quickStats, setQuickStats] = useState({ pendingTasks: 0, overdueInvoices: 0, lowStockItems: 0, pendingApprovals: 0, newLeads: 0, upcomingPayroll: '\u2014' });
  const [modules, setModules] = useState(ERP_MODULES);
  const [favorites, setFavorites] = useState(['crm', 'finance', 'analytics']);

  // UI state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [syncStatus, setSyncStatus] = useState('synced');

  // Modal visibility
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // AI chat state
  const [aiInput, setAiInput] = useState('');

  // Animations
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const aiScaleAnim = useRef(new Animated.Value(0)).current;

  // ── Lifecycle ──
  useEffect(() => {
    init();
    startAnimations();
  }, []);

  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showNotifications || showSettings || showAIChat || showQuickAdd || showExport || showProfile || showAnalytics) {
        setShowNotifications(false); setShowSettings(false); setShowAIChat(false);
        setShowQuickAdd(false); setShowExport(false); setShowProfile(false); setShowAnalytics(false);
        return true;
      }
      return false;
    });
    return () => sub.remove();
  }, [showNotifications, showSettings, showAIChat, showQuickAdd, showExport, showProfile, showAnalytics]);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
      Animated.spring(slideAnim, { toValue: 0, friction: 8, useNativeDriver: true }),
    ]).start();
    setTimeout(() => Animated.spring(aiScaleAnim, { toValue: 1, friction: 6, useNativeDriver: true }).start(), 800);
    Animated.loop(Animated.sequence([
      Animated.timing(pulseAnim, { toValue: 1.08, duration: 1000, useNativeDriver: true }),
      Animated.timing(pulseAnim, { toValue: 1, duration: 1000, useNativeDriver: true }),
    ])).start();
  };

  const init = async () => {
    setLoading(true);
    try {
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          if (data.overview) setOverview(data.overview);
          if (data.activities) setActivities(data.activities);
          if (data.quickStats) setQuickStats(data.quickStats);
          setLoading(false); return;
        }
      }
    } catch {}
    try { const cp = await AsyncStorage.getItem('company_profile'); if (cp) { const p = JSON.parse(cp); setUser(prev => ({ ...prev, name: p.name||'', email: p.email||'', avatar: (p.name||'U').substring(0,2).toUpperCase() })); } } catch {}
    const _erpCid = await AsyncStorage.getItem('company_id'); CACHE_KEY = 'erp_system_cache_v4_' + (_erpCid||'');
    await loadData();
  };

  const loadData = async () => {
    try {
      const cid = await AsyncStorage.getItem('company_id');
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const h = { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: 'Bearer ' + token } : {}) };
      const base = API_BASE;
      const q = (table) => fetch(base + '/api/data/' + table + '?company_id=' + (cid||0) + '&limit=500', { headers: h }).then(r => r.json()).catch(() => ({ data: [] }));
      const [empD, invD, expD, cliD, prjD] = await Promise.all([q('employees'), q('financial_invoices'), q('financial_expenses'), q('clients'), q('projects')]);
      const emps = empD?.data || []; const invs = invD?.data || []; const exps = expD?.data || [];
      const clients = cliD?.data || []; const projs = prjD?.data || [];
      const totalRev = invs.reduce((s, i) => s + parseFloat(i.total_amount || i.amount || 0), 0);
      const totalExp = exps.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
      const profit = totalRev - totalExp;
      const fK = (v) => v >= 1e6 ? '\u00A3'+(v/1e6).toFixed(1)+'M' : v >= 1e3 ? '\u00A3'+(v/1e3).toFixed(0)+'k' : '\u00A3'+v.toFixed(0);
      setOverview({ revenue: fK(totalRev), growth: totalRev > 0 ? '+'+ ((profit/Math.max(totalRev,1))*100).toFixed(1)+'%' : '0%', customers: clients.length, employees: emps.length, projects: projs.length, satisfaction: '\u2014', profitMargin: totalRev > 0 ? ((profit/totalRev)*100).toFixed(1)+'%' : '0%', cashFlow: fK(profit) });
      const acts = [];
      invs.slice(0,3).forEach((inv,i) => acts.push({ id:'inv_'+i, type:'finance', icon:'cash', title:'Invoice: '+(inv.invoice_number||'N/A'), sub:fK(parseFloat(inv.amount||0))+' \u00B7 '+(inv.status||'draft'), time:inv.created_at?new Date(inv.created_at).toLocaleDateString():'', color:'#10b981' }));
      exps.slice(0,2).forEach((exp,i) => acts.push({ id:'exp_'+i, type:'finance', icon:'cash-minus', title:'Expense: '+(exp.description||'N/A'), sub:fK(parseFloat(exp.amount||0)), time:'', color:'#ef4444' }));
      if (!acts.length) acts.push({ id:'none', type:'info', icon:'information', title:'No recent activity', sub:'Start adding data to see activity here', time:'', color:'#94a3b8' });
      setActivities(acts);
      setQuickStats({ pendingTasks: projs.filter(p=>(p.status||'')==='active').length, overdueInvoices: invs.filter(i=>(i.status||'')==='overdue').length, lowStockItems:0, pendingApprovals:0, newLeads: clients.filter(c=>{ const d=new Date(c.created_at); return (Date.now()-d.getTime())<7*864e5; }).length, upcomingPayroll:'\u2014' });
      setNotifications([]);
      console.log('[ERP] Loaded: '+emps.length+' emps, '+invs.length+' invs, '+clients.length+' clients');
    } catch (e) { console.warn('[ERP] loadData error:', e.message); }
    await saveCache();
    setLoading(false);
  };

  const saveCache = async () => {
    try {
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: { overview, activities, quickStats } }));
    } catch {}
  };

  const onRefresh = async () => {
    setRefreshing(true); setSyncStatus('syncing');
    await loadData();
    setRefreshing(false); setSyncStatus('synced');
  };

  const toggleDark = async () => {
    const v = !darkMode;
    setDarkMode(v);
    await AsyncStorage.setItem('erp_dark_mode', v ? 'dark' : 'light');
  };

  const toggleFavorite = async (moduleId) => {
    const updated = favorites.includes(moduleId) ? favorites.filter(f => f !== moduleId) : [...favorites, moduleId];
    setFavorites(updated);
    await AsyncStorage.setItem('erp_favorites', JSON.stringify(updated));
  };

  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const unreadCount = notifications.filter(n => !n.read).length;

  // ── AI Chat ──
  const sendAI = async (msg) => {
    const q = (msg || aiInput).trim();
    if (!q || aiLoading) return;
    setAiInput('');
    setAiHistory(prev => [...prev, { role: 'user', text: q, ts: Date.now() }]);
    setAiLoading(true);
    try {
      // Resolve company name from cached profile (no undefined refs).
      let coName = '';
      try {
        const cp = await AsyncStorage.getItem('company_profile');
        if (cp) coName = JSON.parse(cp)?.name || '';
      } catch {}

      // Build a data block from the live overview + quickStats already loaded.
      const dataBlock = [
        `Company: ${coName || 'this business'}`,
        `Revenue: ${overview.revenue} (growth ${overview.growth})`,
        `Profit margin: ${overview.profitMargin}`,
        `Cash flow: ${overview.cashFlow}`,
        `Customers/Clients: ${overview.customers}`,
        `Employees: ${overview.employees}`,
        `Projects: ${overview.projects}`,
        `Pending tasks: ${quickStats.pendingTasks}`,
        `Overdue invoices: ${quickStats.overdueInvoices}`,
        `Low-stock items: ${quickStats.lowStockItems}`,
        `Pending approvals: ${quickStats.pendingApprovals}`,
      ].join('\n');

      const prompt =
        `You are an expert UK business analyst for JobEng ERP. Here is the company's live data:\n\n` +
        `${dataBlock}\n\n` +
        `User question: ${q}\n\n` +
        `Answer concisely and specifically, referencing the actual numbers above. Use short paragraphs or bullets.`;

      const token = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('userToken')) || '';
      const res = await fetch('https://api.jobeng.ai/api/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt, max_tokens: 700, temperature: 0.7 }),
      });
      const data = await res.json();
      const text = data?.text || data?.content || data?.choices?.[0]?.message?.content || '';
      if (!res.ok || !text) throw new Error(data?.error || `HTTP ${res.status}`);
      setAiHistory(prev => [...prev, { role: 'assistant', text, ts: Date.now() }]);
    } catch (e) {
      setAiHistory(prev => [...prev, { role: 'assistant', text: `Sorry, I couldn't analyse that right now (${e.message}). Please try again.`, ts: Date.now() }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleExport = async (format) => {
    await Share.share({ message: `JobEng ERP Export (${format.toUpperCase()})\n\nRevenue: ${overview.revenue}\nCustomers: ${overview.customers}\nEmployees: ${overview.employees}\nProjects: ${overview.projects}\nGenerated: ${new Date().toLocaleString()}`, title: 'ERP System Export' });
    setShowExport(false);
  };

  const navigateToModule = (module) => {
    navigation?.navigate?.('ERPModule', { module });
  };

  // ── Filtered modules ──
  const categories = ['All', 'Sales', 'Core', 'Operations', 'Intelligence'];
  const filteredModules = useMemo(() => {
    let m = [...modules];
    if (categoryFilter !== 'All') m = m.filter(x => x.category === categoryFilter);
    if (activeTab === 'favorites') m = m.filter(x => favorites.includes(x.id));
    if (searchQuery) m = m.filter(x => String(x.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) || String(x.desc ?? '').toLowerCase().includes(searchQuery.toLowerCase()));
    return m;
  }, [modules, categoryFilter, activeTab, searchQuery, favorites]);

  if (loading) {
    return (
      <View style={{ flex: 1, backgroundColor: T.bg, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={T.blue} />
        <Text style={{ marginTop: 16, color: T.sub, fontSize: 14, fontWeight: '600' }}>Loading ERP System...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
      <StatusBar barStyle={darkMode ? 'light-content' : 'dark-content'} backgroundColor={T.blue} />

      {/* ── Header ── */}
      <LinearGradient colors={[T.blue, '#163b6c']} style={sysStyles.header}>
        <View style={sysStyles.headerRow}>
          {navigation?.canGoBack?.() && (
            <TouchableOpacity style={sysStyles.backBtn} onPress={() => navigation.goBack()}>
              <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
            </TouchableOpacity>
          )}
          <TouchableOpacity style={sysStyles.avatarBtn} onPress={() => setShowProfile(true)}>
            <LinearGradient colors={[T.mint, T.canary]} style={sysStyles.avatar}>
              <Text style={sysStyles.avatarText}>{user.avatar}</Text>
            </LinearGradient>
            <View style={[sysStyles.onlineDot, { backgroundColor: T.success }]} />
          </TouchableOpacity>
          <View style={{ flex: 1 }}>
            <Text style={sysStyles.headerTitle}>JobEng ERP</Text>
            <Text style={sysStyles.headerSub}>{user.name} · {user.role} · {syncStatus === 'synced' ? '✓ Live' : syncStatus === 'syncing' ? '⟳ Syncing' : '✗ Offline'}</Text>
          </View>
          <View style={sysStyles.headerActions}>
            <TouchableOpacity style={sysStyles.headerBtn} onPress={() => setShowNotifications(true)}>
              <MaterialCommunityIcons name="bell-outline" size={20} color="#fff" />
              {unreadCount > 0 && <View style={sysStyles.badge}><Text style={sysStyles.badgeText}>{unreadCount}</Text></View>}
            </TouchableOpacity>
            <TouchableOpacity style={sysStyles.headerBtn} onPress={() => setShowSettings(true)}>
              <MaterialCommunityIcons name="cog-outline" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={sysStyles.headerBtn} onPress={toggleDark}>
              <MaterialCommunityIcons name={darkMode ? 'white-balance-sunny' : 'moon-waning-crescent'} size={18} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Tab Bar */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 10, marginBottom: -6 }}>
          {[
            { id: 'dashboard', icon: 'view-dashboard', label: 'Dashboard' },
            { id: 'modules', icon: 'apps', label: 'Modules' },
            { id: 'favorites', icon: 'star', label: 'Favourites' },
            { id: 'activities', icon: 'timeline', label: 'Activity' },
            { id: 'analytics', icon: 'chart-bar', label: 'Analytics' },
          ].map(tab => (
            <TouchableOpacity key={tab.id} style={[sysStyles.tab, activeTab === tab.id && sysStyles.tabActive]} onPress={() => setActiveTab(tab.id)}>
              <MaterialCommunityIcons name={tab.icon} size={13} color={activeTab === tab.id ? T.blue : 'rgba(255,255,255,0.75)'} />
              <Text style={[sysStyles.tabText, activeTab === tab.id && { color: T.blue }]}>{tab.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </LinearGradient>

      <Animated.ScrollView
        style={{ opacity: fadeAnim }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={T.blue} colors={[T.blue]} />}
      >
        {/* ── DASHBOARD TAB ── */}
        {activeTab === 'dashboard' && (
          <View>
            {/* Overview KPI Cards */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, paddingVertical: 16 }}>
              {[
                { label: 'Revenue', value: overview.revenue, sub: overview.growth, icon: 'trending-up', color: T.success },
                { label: 'Customers', value: overview.customers.toLocaleString(), sub: '+47 this month', icon: 'account-group', color: T.blue },
                { label: 'Employees', value: overview.employees.toString(), sub: '+12 hired', icon: 'account-multiple', color: T.purple },
                { label: 'Projects', value: overview.projects.toString(), sub: '87% on-time', icon: 'clipboard-check', color: T.mint },
                { label: 'Satisfaction', value: overview.satisfaction, sub: 'NPS: 72', icon: 'star', color: T.canary },
                { label: 'Cash Flow', value: overview.cashFlow, sub: 'healthy', icon: 'bank', color: T.success },
              ].map((kpi, i) => (
                <View key={i} style={[sysStyles.kpiCard, { backgroundColor: T.surface, borderColor: T.line }]}>
                  <View style={[sysStyles.kpiIcon, { backgroundColor: kpi.color + '18' }]}>
                    <MaterialCommunityIcons name={kpi.icon} size={18} color={kpi.color} />
                  </View>
                  <Text style={[sysStyles.kpiValue, { color: kpi.color }]}>{kpi.value}</Text>
                  <Text style={[sysStyles.kpiLabel, { color: T.text }]}>{kpi.label}</Text>
                  <Text style={[sysStyles.kpiSub, { color: T.sub }]}>{kpi.sub}</Text>
                </View>
              ))}
              <View style={{ width: 16 }} />
            </ScrollView>

            {/* Quick Stats Row */}
            <View style={[sysStyles.alertsRow, { backgroundColor: T.surface, borderColor: T.line }]}>
              {[
                { label: 'Pending Tasks', value: quickStats.pendingTasks, icon: 'checkbox-marked', color: T.blue },
                { label: 'Overdue Inv.', value: quickStats.overdueInvoices, icon: 'clock-alert', color: T.danger },
                { label: 'Low Stock', value: quickStats.lowStockItems, icon: 'alert', color: T.warning },
                { label: 'Approvals', value: quickStats.pendingApprovals, icon: 'clock-check', color: T.mint },
              ].map((s, i) => (
                <View key={i} style={sysStyles.alertItem}>
                  <MaterialCommunityIcons name={s.icon} size={18} color={s.color} />
                  <Text style={[sysStyles.alertValue, { color: s.color }]}>{s.value}</Text>
                  <Text style={[sysStyles.alertLabel, { color: T.sub }]}>{s.label}</Text>
                </View>
              ))}
            </View>

            {/* AI Insight */}
            <TouchableOpacity style={sysStyles.aiInsightCard} onPress={() => setShowAIChat(true)}>
              <LinearGradient colors={['#6366f1', '#8b5cf6']} style={sysStyles.aiInsightGrad}>
                <MaterialCommunityIcons name="robot-outline" size={22} color="#fff" />
                <View style={{ flex: 1 }}>
                  <Text style={sysStyles.aiInsightTitle}>AI Business Insights</Text>
                  <Text style={sysStyles.aiInsightText} numberOfLines={2}>
                    Revenue is growing at {overview.growth}. {quickStats.lowStockItems} stock alerts need attention. {quickStats.overdueInvoices} invoices are overdue. Tap to ask AI anything about your business.
                  </Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="rgba(255,255,255,0.7)" />
              </LinearGradient>
            </TouchableOpacity>

            {/* Quick Actions */}
            <View style={{ paddingHorizontal: 16, marginBottom: 20 }}>
              <Text style={[sysStyles.sectionTitle, { color: T.text }]}>Quick Actions</Text>
              <View style={sysStyles.actionsGrid}>
                {[
                  { label: 'AI Assistant', icon: 'robot', color: '#6366f1', onPress: () => setShowAIChat(true) },
                  { label: 'Analytics', icon: 'chart-bar', color: T.blue, onPress: () => setShowAnalytics(true) },
                  { label: 'Add Deal', icon: 'briefcase-plus', color: '#f97316', onPress: () => navigateToModule(ERP_MODULES[0]) },
                  { label: 'New Invoice', icon: 'file-document-plus', color: T.success, onPress: () => navigateToModule(ERP_MODULES[1]) },
                  { label: 'Add Employee', icon: 'account-plus', color: T.purple, onPress: () => navigateToModule(ERP_MODULES[2]) },
                  { label: 'Stock Check', icon: 'warehouse', color: T.mint, onPress: () => navigateToModule(ERP_MODULES[3]) },
                  { label: 'Payroll Run', icon: 'currency-gbp', color: '#ec4899', onPress: () => navigateToModule(ERP_MODULES[6]) },
                  { label: 'New PO', icon: 'truck-delivery', color: '#f59e0b', onPress: () => navigateToModule(ERP_MODULES[7]) },
                  { label: 'Work Order', icon: 'factory', color: '#ef4444', onPress: () => navigateToModule(ERP_MODULES[8]) },
                  { label: 'Export Data', icon: 'export', color: T.sub, onPress: () => setShowExport(true) },
                  { label: 'CRM Hub', icon: 'handshake', color: '#f97316', onPress: () => navigation?.navigate?.('CRMScreen') },
                  { label: 'Scale-Up', icon: 'rocket', color: '#8b5cf6', onPress: () => navigation?.navigate?.('ScaleUpBusinessScreen') },
                ].map((a, i) => (
                  <TouchableOpacity key={i} style={sysStyles.actionBtn} onPress={a.onPress} activeOpacity={0.75}>
                    <View style={[sysStyles.actionIcon, { backgroundColor: a.color + '18' }]}>
                      <MaterialCommunityIcons name={a.icon} size={22} color={a.color} />
                    </View>
                    <Text style={[sysStyles.actionLabel, { color: T.text }]} numberOfLines={2}>{a.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Recent Activity Preview */}
            <View style={{ paddingHorizontal: 16, marginBottom: 20 }}>
              <View style={sysStyles.sectionHeaderRow}>
                <Text style={[sysStyles.sectionTitle, { color: T.text }]}>Recent Activity</Text>
                <TouchableOpacity onPress={() => setActiveTab('activities')}>
                  <Text style={[sysStyles.viewAll, { color: T.blue }]}>View All</Text>
                </TouchableOpacity>
              </View>
              {activities.slice(0, 4).map(act => (
                <View key={act.id} style={[sysStyles.activityItem, { backgroundColor: T.surface, borderColor: T.line }]}>
                  <View style={[sysStyles.actIcon, { backgroundColor: act.color + '20' }]}>
                    <MaterialCommunityIcons name={act.icon} size={16} color={act.color} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[sysStyles.actTitle, { color: T.text }]}>{act.title}</Text>
                    <Text style={[sysStyles.actSub, { color: T.sub }]} numberOfLines={1}>{act.sub}</Text>
                  </View>
                  <Text style={[sysStyles.actTime, { color: T.sub }]}>{act.time}</Text>
                </View>
              ))}
            </View>

            {/* Integration strip */}
            <View style={{ paddingHorizontal: 16, marginBottom: 30 }}>
              <Text style={[sysStyles.sectionTitle, { color: T.text }]}>Connected Platforms</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {[
                  { label: 'CRM Hub', sub: 'Pipeline & deals', icon: 'handshake', color: '#f97316', screen: 'CRMScreen' },
                  { label: 'Scale-Up', sub: 'Growth strategy', icon: 'rocket', color: '#8b5cf6', screen: 'ScaleUpBusinessScreen' },
                  { label: 'ML Platform', sub: 'AI & predictions', icon: 'memory', color: '#667eea', screen: 'MachineLearningScreen' },
                ].map((p, i) => (
                  <TouchableOpacity key={i} style={[sysStyles.integrationCard, { backgroundColor: T.surface, borderColor: p.color + '40' }]} onPress={() => navigation?.navigate?.(p.screen)}>
                    <LinearGradient colors={[p.color + '20', p.color + '08']} style={sysStyles.integrationGrad}>
                      <MaterialCommunityIcons name={p.icon} size={22} color={p.color} />
                      <Text style={[sysStyles.integrationTitle, { color: p.color }]}>{p.label}</Text>
                      <Text style={[sysStyles.integrationSub, { color: T.sub }]}>{p.sub}</Text>
                    </LinearGradient>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        )}

        {/* ── MODULES / FAVORITES TAB ── */}
        {(activeTab === 'modules' || activeTab === 'favorites') && (
          <View>
            {/* Search */}
            <View style={[sysStyles.searchBar, { backgroundColor: T.surface, borderColor: T.line, margin: 16 }]}>
              <MaterialCommunityIcons name="magnify" size={18} color={T.sub} />
              <TextInput
                value={searchQuery} onChangeText={setSearchQuery}
                placeholder="Search modules..." placeholderTextColor={T.sub}
                style={[sysStyles.searchInput, { color: T.text }]}
              />
              {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><MaterialCommunityIcons name="close-circle" size={16} color={T.sub} /></TouchableOpacity> : null}
              <TouchableOpacity style={[sysStyles.viewModeBtn, { backgroundColor: T.line }]} onPress={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')}>
                <MaterialCommunityIcons name={viewMode === 'grid' ? 'view-list' : 'view-grid'} size={16} color={T.blue} />
              </TouchableOpacity>
            </View>

            {/* Category chips */}
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, marginBottom: 14 }}>
              {categories.map(cat => (
                <TouchableOpacity key={cat} style={[sysStyles.catChip, categoryFilter === cat && { backgroundColor: T.blue + '20', borderColor: T.blue }, { borderColor: T.line, backgroundColor: T.surface }]} onPress={() => setCategoryFilter(cat)}>
                  <Text style={[sysStyles.catChipText, { color: categoryFilter === cat ? T.blue : T.sub }]}>{cat}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {/* Modules Grid/List */}
            {viewMode === 'grid' ? (
              <View style={sysStyles.modulesGrid}>
                {filteredModules.map(module => (
                  <TouchableOpacity
                    key={module.id}
                    style={[sysStyles.moduleCard, { backgroundColor: T.surface, borderColor: T.line }]}
                    onPress={() => navigateToModule(module)}
                    onLongPress={() => {
                      Alert.alert(module.name, `${module.desc}\n\nVersion: ${module.version}\nCategory: ${module.category}`, [
                        { text: favorites.includes(module.id) ? 'Remove from Favourites' : 'Add to Favourites', onPress: () => toggleFavorite(module.id) },
                        { text: 'Open Module', onPress: () => navigateToModule(module) },
                        { text: 'Cancel', style: 'cancel' },
                      ]);
                    }}
                    activeOpacity={0.75}
                  >
                    <View style={[sysStyles.moduleIcon, { backgroundColor: module.color + '18' }]}>
                      <MaterialCommunityIcons name={module.icon} size={26} color={module.color} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Text style={[sysStyles.moduleName, { color: T.text }]} numberOfLines={1}>{module.name}</Text>
                        {favorites.includes(module.id) && <MaterialCommunityIcons name="star" size={12} color={T.canary} />}
                      </View>
                      <Text style={[sysStyles.moduleDesc, { color: T.sub }]} numberOfLines={1}>{module.desc}</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <View style={[sysStyles.loadBar, { flex: 1 }]}>
                          <View style={[sysStyles.loadFill, { width: `${module.load}%`, backgroundColor: module.load > 80 ? T.danger : module.load > 60 ? T.warning : module.color }]} />
                        </View>
                        <Text style={[sysStyles.loadText, { color: T.sub }]}>{module.load}%</Text>
                      </View>
                    </View>
                    <MaterialCommunityIcons name="chevron-right" size={18} color={T.line} />
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16 }}>
                {filteredModules.map(module => (
                  <TouchableOpacity key={module.id} style={[sysStyles.moduleCardCompact, { backgroundColor: T.surface, borderColor: T.line }]} onPress={() => navigateToModule(module)}>
                    <View style={[sysStyles.moduleIconSm, { backgroundColor: module.color + '18' }]}>
                      <MaterialCommunityIcons name={module.icon} size={20} color={module.color} />
                    </View>
                    <Text style={[sysStyles.moduleNameSm, { color: T.text }]} numberOfLines={2}>{module.name}</Text>
                    <Text style={[sysStyles.moduleCat, { color: module.color, backgroundColor: module.color + '15' }]}>{module.category}</Text>
                    {module.kpis?.slice(0, 2).map((kpi, ki) => (
                      <View key={ki} style={sysStyles.moduleKpiRow}>
                        <Text style={[sysStyles.moduleKpiLabel, { color: T.sub }]}>{kpi.label}</Text>
                        <Text style={[sysStyles.moduleKpiValue, { color: T.text }]}>{kpi.value}</Text>
                      </View>
                    ))}
                  </TouchableOpacity>
                ))}
                <View style={{ width: 16 }} />
              </ScrollView>
            )}
            <View style={{ height: 30 }} />
          </View>
        )}

        {/* ── ACTIVITIES TAB ── */}
        {activeTab === 'activities' && (
          <View style={{ padding: 16 }}>
            <Text style={[sysStyles.sectionTitle, { color: T.text, marginBottom: 14 }]}>Activity Log ({activities.length})</Text>
            {activities.map(act => (
              <View key={act.id} style={[sysStyles.activityItemFull, { backgroundColor: T.surface, borderColor: T.line }]}>
                <View style={[sysStyles.actIconFull, { backgroundColor: act.color + '20' }]}>
                  <MaterialCommunityIcons name={act.icon} size={18} color={act.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[sysStyles.actTitle, { color: T.text }]}>{act.title}</Text>
                  <Text style={[sysStyles.actSub, { color: T.sub }]}>{act.sub}</Text>
                  <Text style={[sysStyles.actTime, { color: T.sub, marginTop: 4 }]}>{act.time}</Text>
                </View>
                <View style={[sysStyles.actTypeBadge, { backgroundColor: act.color + '18' }]}>
                  <Text style={[sysStyles.actTypeText, { color: act.color }]}>{act.type}</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeTab === 'analytics' && (
          <View style={{ padding: 16 }}>
            <Text style={[sysStyles.sectionTitle, { color: T.text, marginBottom: 14 }]}>Business Analytics</Text>

            {/* Revenue Chart */}
            <View style={[sysStyles.analyticsCard, { backgroundColor: T.surface, borderColor: T.line }]}>
              <Text style={[sysStyles.analyticsTitle, { color: T.text }]}>Monthly Revenue vs Target (£M)</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={sysStyles.barChart}>
                  {[].map((m, i) => {
                    const maxV = Math.max(...[].map(x => Math.max(x.revenue, x.target)));
                    const revH = Math.max((m.revenue / maxV) * 140, 4);
                    const tarH = Math.max((m.target / maxV) * 140, 4);
                    const beat = m.revenue >= m.target;
                    return (
                      <View key={i} style={sysStyles.barGroup}>
                        <View style={sysStyles.bars}>
                          <View style={[sysStyles.barTarget, { height: tarH }]} />
                          <View style={[sysStyles.barRevenue, { height: revH, backgroundColor: beat ? T.success : T.blue }]} />
                        </View>
                        <Text style={[sysStyles.barLabel, { color: T.sub }]}>{m.month}</Text>
                        <Text style={[sysStyles.barValue, { color: beat ? T.success : T.blue }]}>£{m.revenue}M</Text>
                      </View>
                    );
                  })}
                </View>
              </ScrollView>
              <View style={sysStyles.barLegend}>
                <View style={sysStyles.legendItem}><View style={[sysStyles.legendDot, { backgroundColor: T.blue }]} /><Text style={[sysStyles.legendText, { color: T.sub }]}>Revenue</Text></View>
                <View style={sysStyles.legendItem}><View style={[sysStyles.legendDot, { backgroundColor: T.line }]} /><Text style={[sysStyles.legendText, { color: T.sub }]}>Target</Text></View>
                <View style={sysStyles.legendItem}><View style={[sysStyles.legendDot, { backgroundColor: T.success }]} /><Text style={[sysStyles.legendText, { color: T.sub }]}>Beat Target</Text></View>
              </View>
            </View>

            {/* Module Usage */}
            <View style={[sysStyles.analyticsCard, { backgroundColor: T.surface, borderColor: T.line }]}>
              <Text style={[sysStyles.analyticsTitle, { color: T.text }]}>Module Usage & Load</Text>
              {ERP_MODULES.slice(0, 6).map(m => (
                <View key={m.id} style={sysStyles.usageRow}>
                  <View style={[sysStyles.usageIcon, { backgroundColor: m.color + '18' }]}>
                    <MaterialCommunityIcons name={m.icon} size={14} color={m.color} />
                  </View>
                  <Text style={[sysStyles.usageName, { color: T.text }]} numberOfLines={1}>{m.name}</Text>
                  <View style={sysStyles.usageBarOuter}>
                    <View style={[sysStyles.usageBarInner, { width: `${m.load}%`, backgroundColor: m.load > 80 ? T.danger : m.load > 60 ? T.warning : m.color }]} />
                  </View>
                  <Text style={[sysStyles.usagePct, { color: m.load > 80 ? T.danger : T.sub }]}>{m.load}%</Text>
                </View>
              ))}
            </View>

            {/* KPI Summary */}
            <View style={[sysStyles.analyticsCard, { backgroundColor: T.surface, borderColor: T.line }]}>
              <Text style={[sysStyles.analyticsTitle, { color: T.text }]}>Key Business KPIs</Text>
              <View style={sysStyles.kpiSummaryGrid}>
                {[
                  { l: 'Revenue YTD', v: '£8.4M', c: T.success }, { l: 'Revenue Growth', v: '+18.2%', c: T.success },
                  { l: 'Profit Margin', v: '22.9%', c: T.blue }, { l: 'Cash Flow', v: '£450k', c: T.success },
                  { l: 'Customer CSAT', v: '92%', c: T.canary }, { l: 'Employee NPS', v: '78', c: T.purple },
                  { l: 'On-Time Delivery', v: '87%', c: T.mint }, { l: 'Inventory Turn', v: '4.2x', c: T.info },
                ].map((k, i) => (
                  <View key={i} style={[sysStyles.kpiSummaryItem, { backgroundColor: T.bg, borderColor: T.line }]}>
                    <Text style={[sysStyles.kpiSummaryValue, { color: k.c }]}>{k.v}</Text>
                    <Text style={[sysStyles.kpiSummaryLabel, { color: T.sub }]}>{k.l}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        <View style={{ height: 100 }} />
      </Animated.ScrollView>

      {/* ── FAB ── */}
      <Animated.View style={[sysStyles.fab, { transform: [{ scale: pulseAnim }] }]}>
        <TouchableOpacity onPress={() => setShowQuickAdd(true)} activeOpacity={0.9}>
          <LinearGradient colors={[T.blue, T.mint]} style={sysStyles.fabInner}>
            <MaterialCommunityIcons name="plus" size={26} color="#fff" />
          </LinearGradient>
        </TouchableOpacity>
      </Animated.View>
      <Animated.View style={[sysStyles.aiFab, { transform: [{ scale: aiScaleAnim }] }]}>
        <TouchableOpacity onPress={() => setShowAIChat(true)} activeOpacity={0.9}>
          <LinearGradient colors={['#6366f1', '#8b5cf6']} style={sysStyles.aiFabInner}>
            <MaterialCommunityIcons name="robot" size={22} color="#fff" />
          </LinearGradient>
        </TouchableOpacity>
      </Animated.View>

      {/* ─────────── MODALS ─────────── */}

      {/* NOTIFICATIONS */}
      <Modal visible={showNotifications} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowNotifications(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <View style={{ backgroundColor: T.blue, flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity onPress={() => setShowNotifications(false)} style={sysStyles.modalClose}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={sysStyles.modalTitle}>Notifications</Text>
            <TouchableOpacity style={sysStyles.modalAction} onPress={markAllRead}><Text style={sysStyles.modalActionText}>Mark all read</Text></TouchableOpacity>
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {notifications.map(n => (
              <TouchableOpacity key={n.id} style={[sysStyles.notifItem, { backgroundColor: T.surface, borderColor: T.line, opacity: n.read ? 0.65 : 1 }]} onPress={() => setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x))}>
                <View style={[sysStyles.notifIcon, { backgroundColor: n.color + '20' }]}>
                  <MaterialCommunityIcons name={n.icon} size={18} color={n.color} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[sysStyles.notifTitle, { color: T.text }]}>{n.title}</Text>
                  <Text style={[sysStyles.notifMsg, { color: T.sub }]}>{n.msg}</Text>
                  <Text style={[sysStyles.notifTime, { color: T.sub }]}>{n.time}</Text>
                </View>
                {!n.read && <View style={[sysStyles.unreadDot, { backgroundColor: T.blue }]} />}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* AI CHAT */}
      <Modal visible={showAIChat} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAIChat(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <LinearGradient colors={['#6366f1', '#8b5cf6']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity onPress={() => setShowAIChat(false)} style={sysStyles.modalClose}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <View style={{ flex: 1 }}>
              <Text style={sysStyles.modalTitle}>ERP AI Assistant</Text>
              <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>Powered by Claude AI</Text>
            </View>
            <TouchableOpacity onPress={() => setShowAIChat(false)}>
              <MaterialCommunityIcons name="delete-sweep" size={20} color="rgba(255,255,255,0.8)" />
            </TouchableOpacity>
          </LinearGradient>
          <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
            <ScrollView style={{ flex: 1, padding: 16 }} contentContainerStyle={{ paddingBottom: 16 }}>
              {aiHistory.length <= 1 && (
                <View>
                  <Text style={[sysStyles.sectionTitle, { color: T.text, marginBottom: 10 }]}>Suggested Questions</Text>
                  {['Summarise my business performance', 'Which modules need attention?', 'What are my biggest revenue risks?', 'Generate a monthly report', 'Forecast next quarter revenue', 'Which employees are due for review?', 'Analyse inventory health', 'Show me overdue invoices analysis'].map((p, i) => (
                    <TouchableOpacity key={i} style={[sysStyles.aiPrompt, { backgroundColor: T.surface, borderColor: T.line }]} onPress={() => sendAI(p)}>
                      <Text style={[sysStyles.aiPromptText, { color: T.text }]}>{p}</Text>
                      <MaterialCommunityIcons name="arrow-right" size={14} color="#8b5cf6" />
                    </TouchableOpacity>
                  ))}
                </View>
              )}
              {aiHistory.map((msg, i) => (
                <View key={i} style={[sysStyles.chatRow, msg.role === 'user' && { justifyContent: 'flex-end' }]}>
                  {msg.role === 'assistant' && <View style={sysStyles.chatAvatarAI}><MaterialCommunityIcons name="robot" size={14} color="#8b5cf6" /></View>}
                  <View style={[sysStyles.chatBubble, msg.role === 'user' ? sysStyles.chatUser : { backgroundColor: T.surface, borderColor: T.line, borderWidth: 1 }]}>
                    <Text style={[sysStyles.chatText, msg.role === 'user' ? { color: '#fff' } : { color: T.text }]}>{msg.text}</Text>
                    <Text style={[sysStyles.chatTime, msg.role === 'user' ? { color: 'rgba(255,255,255,0.6)' } : { color: T.sub }]}>{new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                  </View>
                </View>
              ))}
              {aiLoading && (
                <View style={sysStyles.chatRow}>
                  <View style={sysStyles.chatAvatarAI}><MaterialCommunityIcons name="robot" size={14} color="#8b5cf6" /></View>
                  <View style={[sysStyles.chatBubble, { backgroundColor: T.surface, borderColor: T.line, borderWidth: 1 }]}><ActivityIndicator size="small" color="#8b5cf6" /></View>
                </View>
              )}
            </ScrollView>
            <View style={[sysStyles.chatInputRow, { backgroundColor: T.surface, borderTopColor: T.line }]}>
              <TextInput style={[sysStyles.chatInput, { backgroundColor: T.bg, borderColor: T.line, color: T.text }]} value={aiInput} onChangeText={setAiInput} placeholder="Ask about your business..." placeholderTextColor={T.sub} multiline />
              <TouchableOpacity style={[sysStyles.chatSend, { backgroundColor: aiInput.trim() ? '#6366f1' : T.line }]} onPress={() => sendAI()} disabled={!aiInput.trim() || aiLoading}>
                <MaterialCommunityIcons name="send" size={18} color={aiInput.trim() ? '#fff' : T.sub} />
              </TouchableOpacity>
            </View>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </Modal>

      {/* SETTINGS */}
      <Modal visible={showSettings} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowSettings(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <View style={{ backgroundColor: T.blue, flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity onPress={() => setShowSettings(false)} style={sysStyles.modalClose}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={sysStyles.modalTitle}>Settings</Text>
            <View style={{ width: 40 }} />
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {[
              { section: 'Appearance', items: [
                { label: 'Dark Mode', sub: 'Toggle dark/light theme', icon: 'theme-light-dark', action: <Switch value={darkMode} onValueChange={toggleDark} trackColor={{ true: T.blue }} /> },
              ]},
              { section: 'Data & Sync', items: [
                { label: 'Sync Now', sub: 'Force refresh all data', icon: 'sync', action: null, onPress: onRefresh },
                { label: 'Clear Cache', sub: 'Remove cached data', icon: 'delete', action: null, onPress: async () => { await AsyncStorage.multiRemove([CACHE_KEY]); Alert.alert('Cache cleared'); } },
                { label: 'Export Data', sub: 'Download business data', icon: 'export', action: null, onPress: () => { setShowSettings(false); setShowExport(true); } },
              ]},
              { section: 'Account', items: [
                { label: 'Profile', sub: user.email, icon: 'account', action: null, onPress: () => { setShowSettings(false); setShowProfile(true); } },
                { label: 'Notifications', sub: `${unreadCount} unread`, icon: 'bell', action: null, onPress: () => { setShowSettings(false); setShowNotifications(true); } },
              ]},
              { section: 'About', items: [
                { label: 'Version', sub: 'v4.2.0 – ERP Elite', icon: 'information', action: null },
                { label: 'Modules Active', sub: `${ERP_MODULES.length} modules`, icon: 'apps', action: null },
              ]},
            ].map(group => (
              <View key={group.section} style={{ marginBottom: 20 }}>
                <Text style={[sysStyles.settingsGroup, { color: T.sub }]}>{group.section}</Text>
                <View style={[sysStyles.settingsSection, { backgroundColor: T.surface, borderColor: T.line }]}>
                  {group.items.map((item, i) => (
                    <TouchableOpacity key={i} style={[sysStyles.settingsItem, { borderBottomColor: T.line, borderBottomWidth: i < group.items.length - 1 ? 1 : 0 }]} onPress={item.onPress}>
                      <View style={[sysStyles.settingsItemIcon, { backgroundColor: T.blue + '15' }]}>
                        <MaterialCommunityIcons name={item.icon} size={16} color={T.blue} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={[sysStyles.settingsLabel, { color: T.text }]}>{item.label}</Text>
                        <Text style={[sysStyles.settingsSub, { color: T.sub }]}>{item.sub}</Text>
                      </View>
                      {item.action || <MaterialCommunityIcons name="chevron-right" size={18} color={T.line} />}
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* EXPORT */}
      <Modal visible={showExport} animationType="fade" transparent onRequestClose={() => setShowExport(false)}>
        <View style={sysStyles.overlay}>
          <View style={[sysStyles.exportCard, { backgroundColor: T.surface }]}>
            <Text style={[sysStyles.exportTitle, { color: T.text }]}>Export Business Data</Text>
            <Text style={[sysStyles.exportSub, { color: T.sub }]}>Choose format to export your ERP data</Text>
            {['CSV', 'PDF', 'Excel', 'JSON'].map(fmt => (
              <TouchableOpacity key={fmt} style={[sysStyles.exportBtn, { backgroundColor: T.blue + '15', borderColor: T.blue }]} onPress={() => handleExport(fmt)}>
                <MaterialCommunityIcons name={fmt === 'PDF' ? 'file-pdf-box' : fmt === 'Excel' ? 'microsoft-excel' : fmt === 'JSON' ? 'code-json' : 'file-delimited'} size={20} color={T.blue} />
                <Text style={[sysStyles.exportBtnText, { color: T.blue }]}>{fmt} Export</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={sysStyles.exportCancel} onPress={() => setShowExport(false)}>
              <Text style={{ color: T.sub, fontWeight: '600' }}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* QUICK ADD */}
      <Modal visible={showQuickAdd} animationType="slide" presentationStyle="formSheet" onRequestClose={() => setShowQuickAdd(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <View style={{ backgroundColor: T.blue, flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity onPress={() => setShowQuickAdd(false)} style={sysStyles.modalClose}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={sysStyles.modalTitle}>Quick Create</Text>
            <View style={{ width: 40 }} />
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <Text style={[sysStyles.sectionTitle, { color: T.text, marginBottom: 14 }]}>Create New Record</Text>
            {[
              { label: 'New Deal (CRM)', icon: 'briefcase-plus', color: '#f97316', module: ERP_MODULES[0] },
              { label: 'New Invoice (Finance)', icon: 'file-document-plus', color: T.success, module: ERP_MODULES[1] },
              { label: 'New Employee (HR)', icon: 'account-plus', color: T.purple, module: ERP_MODULES[2] },
              { label: 'Add Stock Item', icon: 'package-variant-plus', color: T.mint, module: ERP_MODULES[3] },
              { label: 'New Project', icon: 'clipboard-plus', color: '#6366f1', module: ERP_MODULES[5] },
              { label: 'Purchase Order', icon: 'truck-plus', color: '#f59e0b', module: ERP_MODULES[7] },
              { label: 'Work Order', icon: 'factory', color: '#ef4444', module: ERP_MODULES[8] },
              { label: 'Payroll Run', icon: 'currency-gbp', color: '#ec4899', module: ERP_MODULES[6] },
            ].map((item, i) => (
              <TouchableOpacity key={i} style={[sysStyles.quickAddItem, { backgroundColor: T.surface, borderColor: T.line }]} onPress={() => { setShowQuickAdd(false); navigateToModule(item.module); }}>
                <View style={[sysStyles.quickAddIcon, { backgroundColor: item.color + '18' }]}>
                  <MaterialCommunityIcons name={item.icon} size={22} color={item.color} />
                </View>
                <Text style={[sysStyles.quickAddLabel, { color: T.text }]}>{item.label}</Text>
                <MaterialCommunityIcons name="chevron-right" size={18} color={T.line} />
              </TouchableOpacity>
            ))}
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* PROFILE */}
      <Modal visible={showProfile} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowProfile(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: T.bg }}>
          <View style={{ backgroundColor: T.blue, flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12 }}>
            <TouchableOpacity onPress={() => setShowProfile(false)} style={sysStyles.modalClose}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={sysStyles.modalTitle}>My Profile</Text>
            <View style={{ width: 40 }} />
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <View style={[sysStyles.profileHeader, { backgroundColor: T.surface, borderColor: T.line }]}>
              <LinearGradient colors={[T.blue, T.mint]} style={sysStyles.profileAvatar}>
                <Text style={sysStyles.profileAvatarText}>{user.avatar}</Text>
              </LinearGradient>
              <Text style={[sysStyles.profileName, { color: T.text }]}>{user.name}</Text>
              <Text style={[sysStyles.profileRole, { color: T.sub }]}>{user.role} · {user.dept}</Text>
              <Text style={[sysStyles.profileEmail, { color: T.blue }]}>{user.email}</Text>
            </View>
            <View style={[sysStyles.profileStats, { backgroundColor: T.surface, borderColor: T.line }]}>
              {[
                { label: 'Joined', value: new Date(user.joinDate).toLocaleDateString() },
                { label: 'Last Login', value: 'Just now' },
                { label: 'Role', value: user.role },
                { label: 'Department', value: user.dept },
              ].map((s, i) => (
                <View key={i} style={[sysStyles.profileStatRow, { borderBottomColor: T.line, borderBottomWidth: i < 3 ? 1 : 0 }]}>
                  <Text style={[sysStyles.profileStatLabel, { color: T.sub }]}>{s.label}</Text>
                  <Text style={[sysStyles.profileStatValue, { color: T.text }]}>{s.value}</Text>
                </View>
              ))}
            
      {/* AI Assistant FAB */}
      <TouchableOpacity 
        onPress={() => setShowAIChat(true)} 
        style={{ position:'absolute', bottom:20, right:20, width:56, height:56, borderRadius:28, backgroundColor:'#6366F1', alignItems:'center', justifyContent:'center', elevation:8, shadowColor:'#6366F1', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8, zIndex:999 }}>
        <MaterialCommunityIcons name="robot" size={24} color="#fff" />
      </TouchableOpacity>
      <AIChatWidget screenName="ERPSystemScreen" visible={showAIChat} onClose={() => setShowAIChat(false)} title="ERP AI" extraContext={{ overview }} />

    </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const sysStyles = StyleSheet.create({
  header: { paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) : 0, paddingHorizontal: 16, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  avatarBtn: { position: 'relative' },
  avatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  avatarText: { color: '#fff', fontSize: 14, fontWeight: '800' },
  onlineDot: { width: 10, height: 10, borderRadius: 5, borderWidth: 2, borderColor: '#fff', position: 'absolute', bottom: 0, right: 0 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 1 },
  headerActions: { flexDirection: 'row', gap: 4 },
  headerBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center', position: 'relative' },
  badge: { position: 'absolute', top: -2, right: -2, backgroundColor: '#ef4444', borderRadius: 7, minWidth: 14, height: 14, justifyContent: 'center', alignItems: 'center', borderWidth: 1.5, borderColor: '#fff' },
  badgeText: { color: '#fff', fontSize: 9, fontWeight: '800' },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, marginRight: 6, marginBottom: 8 },
  tabActive: { backgroundColor: '#fff' },
  tabText: { color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: '600' },
  // KPI cards
  kpiCard: { width: 130, borderRadius: 16, padding: 14, marginRight: 10, borderWidth: 1, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6 },
  kpiIcon: { width: 34, height: 34, borderRadius: 10, justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
  kpiValue: { fontSize: 20, fontWeight: '800', marginBottom: 2 },
  kpiLabel: { fontSize: 12, fontWeight: '600', marginBottom: 2 },
  kpiSub: { fontSize: 10 },
  // Alerts row
  alertsRow: { flexDirection: 'row', marginHorizontal: 16, borderRadius: 14, padding: 12, borderWidth: 1, marginBottom: 14, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4 },
  alertItem: { flex: 1, alignItems: 'center', gap: 3 },
  alertValue: { fontSize: 18, fontWeight: '800' },
  alertLabel: { fontSize: 9, fontWeight: '500', textAlign: 'center' },
  // AI insight
  aiInsightCard: { marginHorizontal: 16, borderRadius: 16, overflow: 'hidden', marginBottom: 20, elevation: 3, shadowColor: '#6366f1', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 8 },
  aiInsightGrad: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 16 },
  aiInsightTitle: { color: '#fff', fontSize: 13, fontWeight: '700', marginBottom: 3 },
  aiInsightText: { color: 'rgba(255,255,255,0.85)', fontSize: 12, lineHeight: 17 },
  // Section
  sectionTitle: { fontSize: 15, fontWeight: '700', marginBottom: 12 },
  sectionHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  viewAll: { fontSize: 12, fontWeight: '600' },
  // Actions grid
  actionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  actionBtn: { width: (width - 72) / 4, alignItems: 'center', gap: 6 },
  actionIcon: { width: 52, height: 52, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  actionLabel: { fontSize: 10, fontWeight: '600', textAlign: 'center' },
  // Activity
  activityItem: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, borderWidth: 1, marginBottom: 8, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  actIcon: { width: 36, height: 36, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  actTitle: { fontSize: 13, fontWeight: '600', marginBottom: 2 },
  actSub: { fontSize: 11 },
  actTime: { fontSize: 10 },
  activityItemFull: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  actIconFull: { width: 40, height: 40, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  actTypeBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  actTypeText: { fontSize: 10, fontWeight: '700', textTransform: 'uppercase' },
  // Integration
  integrationCard: { marginRight: 12, borderRadius: 14, overflow: 'hidden', borderWidth: 1 },
  integrationGrad: { width: 150, padding: 16, gap: 8, alignItems: 'flex-start' },
  integrationTitle: { fontSize: 14, fontWeight: '700' },
  integrationSub: { fontSize: 11 },
  // Search
  searchBar: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderRadius: 14, borderWidth: 1, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3 },
  searchInput: { flex: 1, fontSize: 14 },
  viewModeBtn: { width: 30, height: 30, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  // Category chips
  catChip: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 20, borderWidth: 1, marginRight: 8 },
  catChipText: { fontSize: 12, fontWeight: '600' },
  // Modules grid (list view)
  modulesGrid: { paddingHorizontal: 16, gap: 10, marginBottom: 16 },
  moduleCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 16, borderWidth: 1, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6 },
  moduleIcon: { width: 50, height: 50, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  moduleName: { fontSize: 14, fontWeight: '700' },
  moduleDesc: { fontSize: 11, marginTop: 2 },
  loadBar: { height: 4, borderRadius: 2, backgroundColor: '#e2e8f0', overflow: 'hidden' },
  loadFill: { height: '100%', borderRadius: 2 },
  loadText: { fontSize: 10, fontWeight: '600' },
  // Modules card compact (horizontal scroll)
  moduleCardCompact: { width: 160, borderRadius: 16, padding: 14, marginRight: 10, borderWidth: 1, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6 },
  moduleIconSm: { width: 40, height: 40, borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
  moduleNameSm: { fontSize: 13, fontWeight: '700', marginBottom: 6 },
  moduleCat: { fontSize: 10, fontWeight: '700', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8, alignSelf: 'flex-start', marginBottom: 10 },
  moduleKpiRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  moduleKpiLabel: { fontSize: 10 },
  moduleKpiValue: { fontSize: 11, fontWeight: '700' },
  // FABs
  fab: { position: 'absolute', bottom: 90, right: 20 },
  fabInner: { width: 56, height: 56, borderRadius: 28, justifyContent: 'center', alignItems: 'center', elevation: 8, shadowColor: '#0e4e96', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 8 },
  aiFab: { position: 'absolute', bottom: 90, right: 86 },
  aiFabInner: { width: 52, height: 52, borderRadius: 26, justifyContent: 'center', alignItems: 'center', elevation: 8, shadowColor: '#6366f1', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 8 },
  // Modal shared
  modalClose: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  modalTitle: { flex: 1, color: '#fff', fontSize: 17, fontWeight: '700' },
  modalAction: { padding: 8 },
  modalActionText: { color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: '600' },
  // Notifications
  notifItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  notifIcon: { width: 40, height: 40, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  notifTitle: { fontSize: 13, fontWeight: '700', marginBottom: 3 },
  notifMsg: { fontSize: 12, lineHeight: 17, marginBottom: 4 },
  notifTime: { fontSize: 10 },
  unreadDot: { width: 8, height: 8, borderRadius: 4, marginTop: 6 },
  // AI Chat
  aiPrompt: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 12, borderRadius: 12, borderWidth: 1, marginBottom: 8 },
  aiPromptText: { fontSize: 13, fontWeight: '500', flex: 1 },
  chatRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, marginBottom: 12 },
  chatAvatarAI: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#ede9fe', justifyContent: 'center', alignItems: 'center' },
  chatBubble: { maxWidth: width * 0.72, padding: 12, borderRadius: 16, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3 },
  chatUser: { backgroundColor: '#6366f1', borderBottomRightRadius: 4 },
  chatText: { fontSize: 13, lineHeight: 19 },
  chatTime: { fontSize: 9, marginTop: 4, textAlign: 'right' },
  chatInputRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 10, borderTopWidth: 1 },
  chatInput: { flex: 1, borderWidth: 1, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, fontSize: 13, maxHeight: 80 },
  chatSend: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  // Settings
  settingsGroup: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, marginLeft: 4 },
  settingsSection: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  settingsItem: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14 },
  settingsItemIcon: { width: 32, height: 32, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  settingsLabel: { fontSize: 14, fontWeight: '600' },
  settingsSub: { fontSize: 11, marginTop: 1 },
  // Export
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  exportCard: { borderRadius: 24, padding: 24, margin: 16, elevation: 10, shadowColor: '#000', shadowOffset: { width: 0, height: -4 }, shadowOpacity: 0.15, shadowRadius: 12 },
  exportTitle: { fontSize: 18, fontWeight: '800', marginBottom: 6, textAlign: 'center' },
  exportSub: { fontSize: 13, textAlign: 'center', marginBottom: 20 },
  exportBtn: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10 },
  exportBtnText: { fontSize: 14, fontWeight: '600' },
  exportCancel: { alignItems: 'center', padding: 14 },
  // Quick add
  quickAddItem: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16, borderRadius: 16, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  quickAddIcon: { width: 48, height: 48, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  quickAddLabel: { flex: 1, fontSize: 14, fontWeight: '600' },
  // Profile
  profileHeader: { borderRadius: 20, padding: 24, alignItems: 'center', borderWidth: 1, marginBottom: 16, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6 },
  profileAvatar: { width: 80, height: 80, borderRadius: 40, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  profileAvatarText: { color: '#fff', fontSize: 28, fontWeight: '800' },
  profileName: { fontSize: 20, fontWeight: '800', marginBottom: 4 },
  profileRole: { fontSize: 13, marginBottom: 4 },
  profileEmail: { fontSize: 13, fontWeight: '600' },
  profileStats: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  profileStatRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 14 },
  profileStatLabel: { fontSize: 13 },
  profileStatValue: { fontSize: 13, fontWeight: '700' },
  // Analytics
  analyticsCard: { borderRadius: 16, padding: 16, borderWidth: 1, marginBottom: 16, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6 },
  analyticsTitle: { fontSize: 14, fontWeight: '700', marginBottom: 16 },
  barChart: { flexDirection: 'row', alignItems: 'flex-end', gap: 10, paddingBottom: 4, height: 160 },
  barGroup: { alignItems: 'center', width: 52 },
  bars: { flexDirection: 'row', alignItems: 'flex-end', gap: 3 },
  barTarget: { width: 22, borderRadius: 4, backgroundColor: '#e2e8f0' },
  barRevenue: { width: 22, borderRadius: 4 },
  barLabel: { fontSize: 10, marginTop: 6, fontWeight: '600' },
  barValue: { fontSize: 10, marginTop: 2, fontWeight: '700' },
  barLegend: { flexDirection: 'row', gap: 16, marginTop: 12 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { fontSize: 11 },
  usageRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  usageIcon: { width: 28, height: 28, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  usageName: { width: 110, fontSize: 12, fontWeight: '600' },
  usageBarOuter: { flex: 1, height: 6, borderRadius: 3, backgroundColor: '#e2e8f0', overflow: 'hidden' },
  usageBarInner: { height: '100%', borderRadius: 3 },
  usagePct: { fontSize: 11, fontWeight: '700', width: 34, textAlign: 'right' },
  kpiSummaryGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  kpiSummaryItem: { width: (width - 80) / 2, padding: 12, borderRadius: 12, borderWidth: 1, alignItems: 'center' },
  kpiSummaryValue: { fontSize: 18, fontWeight: '800', marginBottom: 4 },
  kpiSummaryLabel: { fontSize: 10, fontWeight: '500', textAlign: 'center' },
});

export default EnhancedERPSystemScreen;